import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector, Input, OnInit } from "@angular/core";
import { NoticiasService } from "src/app/servicios/noticias/noticias.service";
import { Autor } from "@src/app/models/interfaces/autor.interface";
import { TipoTarjeta } from "@src/app/models/enums/tipoTarjeta.enum";
import { Blog } from "@src/app/models/interfaces/blog.interface";
import { firstValueFrom } from "rxjs";
import { SkeletonImageComponent } from "../skeleton-image/skeleton-image/skeleton-image.component";
import { Router } from "@angular/router";
import { NgIf } from "@angular/common";
import { BlogService } from "@src/app/servicios/blog/blog.service";

@Component({
  selector: "app-tarjeta-info-autor",
  imports: [SkeletonImageComponent, NgIf],
  templateUrl: "./tarjeta-info-autor.component.html",
  styleUrls: ["./tarjeta-info-autor.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [BlogService],
})
/* La clase TarjetaInfoAutorComponent es un componente que representa una tarjeta de información de un autor */
export class TarjetaInfoAutorComponent implements OnInit {
  @Input()
  public id: string;
  @Input()
  public tipoTarjeta: TipoTarjeta;
  public muestraTarjeta = false;

  /* `data` es un objeto que contiene los datos que se mostrarán en la tarjeta. */
  public data: {
    titulo: string;
    bio?: string;
    foto: string;
    subTitulo?: string;
    redes?: boolean;
    twitter?: string;
    instagram?: string;
  };

  constructor(
    private readonly noticiasService: NoticiasService,
    private readonly blogService: BlogService,
    private readonly injector: Injector,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    switch (this.tipoTarjeta) {
      case TipoTarjeta.Blog:
        this.initBlog();
        break;
      case TipoTarjeta.Redes:
        this.initRedes();
        break;
      default:
        break;
    }
  }

  /**
   * Inicializa los datos de las redes sociales del autor asociado al componente.
   *
   * Este método obtiene la información del autor a partir de un servicio, valida
   * si los datos de las redes sociales son correctos y actualiza la tarjeta con
   * la información del autor. Si los datos no son válidos, oculta la tarjeta.
   *
   * @returns Una promesa que se resuelve cuando la inicialización se completa.
   * @throws Error si ocurre un problema al buscar la información del autor.
   */
  async initRedes(): Promise<void> {
    const authByUserId = this.noticiasService.getAutorByUserId(this.id);
    if (authByUserId) {
      try {
        const autor = await firstValueFrom(authByUserId);

        if (!this.validacionRedes(autor)) {
          this.muestraTarjeta = false;
          this.cdr.detectChanges();
          return;
        }

        this.data = {
          titulo: autor.nombre,
          foto: autor.url_foto_v2,
          bio: autor.bio,
          redes: true,
          twitter: autor.twitter,
          instagram: autor.instagram,
        };
        this.muestraTarjeta = true;
        this.cdr.detectChanges();
      } catch {
        throw new Error("Error al buscar el autor");
      }
    }
  }

  /**
   * Inicializa el blog obteniendo los datos del servicio y configurando la tarjeta de información del autor.
   *
   * Este método realiza una solicitud para obtener los datos del blog asociados a un autor específico.
   * Si los datos obtenidos no son válidos según la validación definida, se oculta la tarjeta.
   * En caso de error al obtener los datos, se redirige al usuario a una página 404.
   *
   * @returns {Promise<void>} Una promesa que se resuelve cuando la inicialización se completa.
   *
   * @throws {Error} Si ocurre un error al obtener los datos del blog.
   */
  async initBlog(): Promise<void> {
    const postsBlog = this.blogService.getPostsBlog(1, this.id, "slug");
    if (postsBlog) {
      try {
        const { blog } = await firstValueFrom(postsBlog);

        if (!this.validacionBlog(blog)) {
          this.muestraTarjeta = false;
          this.cdr.detectChanges();
          return;
        }

        this.data = {
          titulo: blog.blogInfo?.nombreSubseccion,
          subTitulo: `Por ${blog.blogInfo.nombre}`,
          foto: blog.blogInfo.foto,
        };
        this.muestraTarjeta = true;
        this.cdr.detectChanges();
      } catch (error) {
        const router = this.injector.get(Router);
        router.navigate(["/404"]);
      }
    }
  }

  /**
   * La función validacionRedes comprueba si el autor tiene cuentas en redes sociales (Twitter o
   * Instagram) y devuelve verdadero si las tiene, de lo contrario, o si no tiene nombre o foto, devuelve falso.
   * @param autor Objeto de tipo Autor.
   * @returns true si el autor tiene redes o bio, false en caso contrario.
   */
  validacionRedes(autor: Autor): boolean {
    if (autor?.nombre && autor?.url_foto_v2) {
      if (autor?.twitter || autor?.instagram) {
        return true;
      }
      if (autor?.bio) {
        return true;
      }
    }
    return false;
  }

  /**
   * La función validacionBlog verifica si un objeto de blog tiene nombre, foto y nombre de
   * subsección, y devuelve un valor booleano que indica si todas estas propiedades están presentes.
   * @param blog Objeto de tipo Blog.
   * @returns true si el blog es válido, false en caso contrario.
   */
  validacionBlog(blog: Blog): boolean {
    if (!blog?.blogInfo) return false;
    return Boolean(blog?.blogInfo.nombre && blog?.blogInfo.foto && blog?.blogInfo.nombreSubseccion);
  }
}
